// apps/web/src/widgets/option-set/toggle.ts
//
// Phase 11.7 Plan 01 Task 2 — shared pure option-set selection toggle.
//
// Extracted from OptionSetRenderer.toggle (renderer.tsx) so the cardinality
// guards live in ONE place. Plan 02 (RecipeOptionGroup interactive Surface 1)
// and Plan 03 (per-batch picker, Surface 2) reuse this verbatim instead of
// copy-pasting the subtle single-select swap — two copies WILL drift
// (Pitfall 1; 06-REVIEW WR-02 fixed a frozen-selection bug in this exact spot).
//
// PURE: no React import, no side effects. Returns the NEXT selection array.
// A returned same-membership array means "no change" — the caller may re-set
// or guard the side effect; this module never calls onStateChange itself.
//
// Precedence (mirrors the analog renderer.tsx body exactly):
//   1. single-select swap: min===1 && max===1 && !selected.includes(id) → [id]
//   2. selected && at min (selected.length===min) → no-op (cannot unselect)
//   3. selected → remove (filter id out)
//   4. at max (max!==undefined && selected.length===max) → no-op (cannot add)
//   5. otherwise → append id

export function toggle(
  selected: ReadonlyArray<string>,
  id: string,
  bounds: { min: number; max: number | undefined },
): string[] {
  const { min, max } = bounds;
  const isSelected = selected.includes(id);

  // 1. Single-select swap: when min === max === 1, the "exactly-one"
  // cardinality pattern REPLACES the current selection rather than toggling.
  // Without this branch the selection freezes — at min=1 the user can't
  // deselect, at max=1 the user can't pick another option. (06-REVIEW WR-02)
  if (min === 1 && max === 1 && !isSelected) {
    return [id];
  }

  // 2. + 3. Currently selected: remove it, unless we are at the min floor.
  if (isSelected) {
    if (selected.length === min) {
      return [...selected]; // at min — can't unselect further (no-op)
    }
    return selected.filter((x) => x !== id);
  }

  // 4. Not selected and at (or over) the max ceiling — can't select more (no-op).
  // Use >= so an over-max seeded/persisted selection (e.g. a recipe whose
  // cardinality.max was later tightened) is still clamped and cannot grow
  // further. Strict === would let a >max state fall through to the append
  // branch (11.7-REVIEW WR-02).
  const atMax = max !== undefined && selected.length >= max;
  if (atMax) {
    return [...selected];
  }

  // 5. Append.
  return [...selected, id];
}
